/**
 * Where to send someone once they have signed in or signed up.
 *
 * AuthGuard remembers the page it bounced them from — path AND query, so the landing's
 * `/shipment/new?type=passengers` survives — and the auth screens read it back when the session
 * exists. localStorage rather than router state, because two sign-in paths lose `location.state`:
 * the email link opens a new tab, and a mobile social login reloads the page.
 *
 * Only a same-origin relative path is ever stored or returned (no open redirect), and it expires
 * after 30 minutes, so a path left by an abandoned login cannot redirect a later one. A stamp from
 * the future does not count either: a clock set back, or a value edited by hand, must not make an
 * entry last.
 */
const STORAGE_KEY = 'nexvia:return-path';

export const RETURN_PATH_TTL_MS = 30 * 60 * 1000;

// Two tabs' clocks can differ by a little; a stamp further ahead than this is not believed.
const CLOCK_SKEW_ALLOWANCE_MS = 60_000;

/** What is remembered, with the moment it was saved: a screen that holds on to it can check the expiry later. */
export interface ReturnPathEntry {
  path: string;
  savedAt: number;
}

export function isSafeReturnPath(value: unknown): value is string {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return false;
  try {
    return new URL(value, window.location.origin).origin === window.location.origin;
  } catch {
    return false;
  }
}

function isFresh(savedAt: unknown, now: number): savedAt is number {
  return (
    typeof savedAt === 'number' &&
    Number.isFinite(savedAt) &&
    savedAt <= now + CLOCK_SKEW_ALLOWANCE_MS &&
    now - savedAt <= RETURN_PATH_TTL_MS
  );
}

export function rememberReturnPath(path: string, now: number = Date.now()): void {
  if (!isSafeReturnPath(path)) return;
  const entry: ReturnPathEntry = { path, savedAt: now };
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entry));
  } catch {
    // Storage blocked: the dashboard stays the fallback, as before.
  }
}

function read(now: number): ReturnPathEntry | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as Partial<ReturnPathEntry>;
    if (!isFresh(stored.savedAt, now) || !isSafeReturnPath(stored.path)) return null;
    return { path: stored.path, savedAt: stored.savedAt };
  } catch {
    return null;
  }
}

/** The remembered path, left in place — for a screen that navigates while its guard also does. */
export function peekReturnPath(now: number = Date.now()): string | null {
  return read(now)?.path ?? null;
}

/**
 * The remembered entry, left in place, for a screen that has to keep it: another tab can take the
 * stored one first, as the tab that asked for the email link does the moment the session exists.
 */
export function peekReturnPathEntry(now: number = Date.now()): ReturnPathEntry | null {
  return read(now);
}

/** The path of an entry held since earlier, or null once it has expired. */
export function freshReturnPath(entry: ReturnPathEntry | null, now: number = Date.now()): string | null {
  return entry && isFresh(entry.savedAt, now) && isSafeReturnPath(entry.path) ? entry.path : null;
}

/** Forgets whatever is remembered: the end of a session leaves no page to come back to. */
export function forgetReturnPath(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clean.
  }
}

/** The remembered path, removed as it is read: it is used once. */
export function takeReturnPath(now: number = Date.now()): string | null {
  const path = peekReturnPath(now);
  forgetReturnPath();
  return path;
}
